/**
 * MIDI Clip and Note Management
 * Data structures and utilities for MIDI clip manipulation
 */

// Generate unique ID
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Create a MIDI Note
 */
export const createMIDINote = ({
  pitch,
  startTime,
  duration = 0.25,
  velocity = 100,
  id = null,
}) => ({
  id: id || generateId(),
  pitch, // MIDI note number (0-127)
  startTime, // Time in seconds from clip start
  duration, // Note length in seconds
  velocity: Math.max(1, Math.min(127, velocity)),
});

/**
 * Create a MIDI Clip
 */
export const createMIDIClip = ({
  trackId,
  startTime = 0,
  duration = 4,
  notes = [],
  name = 'MIDI Clip',
  id = null,
}) => ({
  id: id || generateId(),
  trackId,
  startTime, // Clip position on timeline (seconds)
  duration, // Clip length (seconds)
  notes: notes.map(n => ({ ...n, id: n.id || generateId() })), // MIDI notes within clip
  name,
  color: `hsl(${Math.random() * 360}, 70%, 50%)`,
});

/**
 * Duplicate a MIDI Clip with new IDs
 */
export const duplicateMIDIClip = (clip, newStartTime = null) => {
  const newClip = createMIDIClip({
    trackId: clip.trackId,
    startTime: newStartTime !== null ? newStartTime : clip.startTime + clip.duration,
    duration: clip.duration,
    notes: clip.notes.map(note => createMIDINote({
      pitch: note.pitch,
      startTime: note.startTime,
      duration: note.duration,
      velocity: note.velocity,
    })),
    name: `${clip.name} (Copy)`,
  });
  return newClip;
};

/**
 * Move a MIDI Clip
 */
export const moveMIDIClip = (clip, newStartTime) => ({
  ...clip,
  startTime: Math.max(0, newStartTime),
});

/**
 * Trim MIDI Clip (adjust start/end)
 */
export const trimMIDIClip = (clip, { newStartTime, newDuration }) => {
  const trimmedClip = { ...clip };
  
  if (newStartTime !== undefined) {
    const delta = newStartTime - clip.startTime;
    trimmedClip.startTime = Math.max(0, newStartTime);
    trimmedClip.duration = Math.max(0.25, clip.duration - delta);
    // Adjust note start times relative to new clip start
    trimmedClip.notes = clip.notes
      .map(note => ({
        ...note,
        startTime: note.startTime - delta,
      }))
      .filter(note => note.startTime >= 0 && note.startTime < trimmedClip.duration);
  }
  
  if (newDuration !== undefined) {
    trimmedClip.duration = Math.max(0.25, newDuration);
    // Remove notes that fall outside the new duration
    trimmedClip.notes = trimmedClip.notes.filter(
      note => note.startTime < trimmedClip.duration
    );
  }
  
  return trimmedClip;
};

/**
 * Quantization Utilities
 */
export const QUANTIZE_VALUES = {
  '1/1': 4,
  '1/2': 2,
  '1/4': 1,
  '1/8': 0.5,
  '1/16': 0.25,
  '1/32': 0.125,
};

/**
 * Calculate grid size in seconds based on BPM and quantize value
 */
export const getGridSize = (bpm, quantizeValue) => {
  const beatsPerSecond = bpm / 60;
  const secondsPerBeat = 1 / beatsPerSecond;
  return secondsPerBeat * QUANTIZE_VALUES[quantizeValue];
};

/**
 * Quantize a single note's start time
 */
export const quantizeNoteTime = (note, gridSize, strength = 1) => {
  const nearestGrid = Math.round(note.startTime / gridSize) * gridSize;
  const delta = nearestGrid - note.startTime;
  const newStartTime = note.startTime + (delta * strength);
  
  return {
    ...note,
    startTime: Math.max(0, newStartTime),
  };
};

/**
 * Quantize all notes in a clip
 */
export const quantizeClipNotes = (clip, bpm, quantizeValue, strength = 1) => {
  const gridSize = getGridSize(bpm, quantizeValue);
  
  return {
    ...clip,
    notes: clip.notes.map(note => quantizeNoteTime(note, gridSize, strength)),
  };
};

/**
 * Add a note to a clip
 */
export const addNoteToClip = (clip, note) => ({
  ...clip,
  notes: [...clip.notes, { ...note, id: note.id || generateId() }],
});

/**
 * Remove a note from a clip
 */
export const removeNoteFromClip = (clip, noteId) => ({
  ...clip,
  notes: clip.notes.filter(n => n.id !== noteId),
});

/**
 * Update a note in a clip
 */
export const updateNoteInClip = (clip, noteId, updates) => ({
  ...clip,
  notes: clip.notes.map(n => 
    n.id === noteId ? { ...n, ...updates } : n
  ),
});

/**
 * Move a note within a clip
 */
export const moveNoteInClip = (clip, noteId, { pitch, startTime }) => {
  return updateNoteInClip(clip, noteId, {
    ...(pitch !== undefined && { pitch: Math.max(0, Math.min(127, pitch)) }),
    ...(startTime !== undefined && { startTime: Math.max(0, startTime) }),
  });
};

/**
 * Resize a note in a clip
 */
export const resizeNoteInClip = (clip, noteId, newDuration) => {
  return updateNoteInClip(clip, noteId, {
    duration: Math.max(0.0625, newDuration), // Minimum 1/64 note
  });
};

/**
 * Split a MIDI Clip at a specific time
 * Returns two clips: [leftClip, rightClip]
 */
export const splitMIDIClip = (clip, splitTime) => {
  // Ensure split time is within clip bounds
  const relativeTime = splitTime - clip.startTime;
  if (relativeTime <= 0 || relativeTime >= clip.duration) {
    return null; // Cannot split at or beyond boundaries
  }
  
  // Notes for left clip (start before split point)
  const leftNotes = clip.notes
    .filter(note => note.startTime < relativeTime)
    .map(note => ({
      ...note,
      id: generateId(),
      // Trim notes that extend past split point
      duration: Math.min(note.duration, relativeTime - note.startTime),
    }));
  
  // Notes for right clip (start at or after split point)
  const rightNotes = clip.notes
    .filter(note => note.startTime + note.duration > relativeTime)
    .map(note => {
      const newStartTime = Math.max(0, note.startTime - relativeTime);
      const trimAmount = Math.max(0, relativeTime - note.startTime);
      return {
        ...note,
        id: generateId(),
        startTime: newStartTime,
        duration: note.duration - trimAmount,
      };
    })
    .filter(note => note.duration > 0);
  
  const leftClip = {
    ...clip,
    id: generateId(),
    duration: relativeTime,
    notes: leftNotes,
    name: `${clip.name} (L)`,
  };
  
  const rightClip = {
    ...clip,
    id: generateId(),
    startTime: splitTime,
    duration: clip.duration - relativeTime,
    notes: rightNotes,
    name: `${clip.name} (R)`,
  };
  
  return [leftClip, rightClip];
};

/**
 * Join/Merge multiple MIDI clips into one
 * Clips must be on the same track and are merged in time order
 */
export const joinMIDIClips = (clips) => {
  if (!clips || clips.length < 2) return null;
  
  // Sort clips by start time
  const sortedClips = [...clips].sort((a, b) => a.startTime - b.startTime);
  
  // Calculate merged clip boundaries
  const mergeStart = sortedClips[0].startTime;
  const mergeEnd = Math.max(...sortedClips.map(c => c.startTime + c.duration));
  
  // Collect all notes with adjusted timing
  const mergedNotes = [];
  sortedClips.forEach(clip => {
    clip.notes.forEach(note => {
      mergedNotes.push({
        ...note,
        id: generateId(),
        // Adjust note start time relative to merged clip start
        startTime: (clip.startTime - mergeStart) + note.startTime,
      });
    });
  });
  
  return {
    id: generateId(),
    trackId: sortedClips[0].trackId,
    startTime: mergeStart,
    duration: mergeEnd - mergeStart,
    notes: mergedNotes,
    name: 'Merged Clip',
    color: sortedClips[0].color,
  };
};

/**
 * Convert legacy MIDI events to clips
 * (for backward compatibility with existing instrument tracks)
 */
export const midiEventsToClip = (events, trackId) => {
  if (!events || events.length === 0) return null;
  
  // Group note-on events with their note-off
  const notes = [];
  const activeNotes = new Map();
  
  events.forEach(event => {
    if (event.type === 'NOTE_ON') {
      activeNotes.set(event.note, {
        pitch: event.note,
        startTime: event.time,
        velocity: event.velocity,
      });
    } else if (event.type === 'NOTE_OFF') {
      const noteOn = activeNotes.get(event.note);
      if (noteOn) {
        notes.push(createMIDINote({
          pitch: noteOn.pitch,
          startTime: noteOn.startTime,
          duration: event.time - noteOn.startTime,
          velocity: noteOn.velocity,
        }));
        activeNotes.delete(event.note);
      }
    }
  });
  
  // Handle notes without note-off (give them default duration)
  activeNotes.forEach((noteOn, pitch) => {
    notes.push(createMIDINote({
      pitch: noteOn.pitch,
      startTime: noteOn.startTime,
      duration: 0.25,
      velocity: noteOn.velocity,
    }));
  });
  
  if (notes.length === 0) return null;
  
  // Calculate clip duration
  const maxEndTime = Math.max(...notes.map(n => n.startTime + n.duration));
  
  return createMIDIClip({
    trackId,
    startTime: 0,
    duration: Math.max(4, Math.ceil(maxEndTime)),
    notes,
    name: 'Recorded MIDI',
  });
};

export default {
  createMIDINote,
  createMIDIClip,
  duplicateMIDIClip,
  moveMIDIClip,
  trimMIDIClip,
  splitMIDIClip,
  joinMIDIClips,
  quantizeClipNotes,
  addNoteToClip,
  removeNoteFromClip,
  updateNoteInClip,
  moveNoteInClip,
  resizeNoteInClip,
  midiEventsToClip,
  getGridSize,
  QUANTIZE_VALUES,
};
